import { type ComponentType, type ReactNode } from 'react';
import { LuSparkle } from 'react-icons/lu';
import { RiEditLine, RiStethoscopeLine } from 'react-icons/ri';
import type { AgentType } from '@/model/Agent.model';

type IconComponent = ComponentType<{ size: number; className: string }>;

const agentIconMap: Record<AgentType, IconComponent> = {
  study_health_check: RiStethoscopeLine,
  study_protocol_parser: RiEditLine,
};

/**
 * Maps each agent type to its kebab-case URL segment. The API exposes agent
 * slugs as snake_case enum values, but web routes use kebab-case, so this map
 * is the single source of truth for translating between the two.
 */
export const agentUrlSlugMap: Record<AgentType, string> = {
  study_health_check: 'study-health-check',
  study_protocol_parser: 'study-protocol-parser',
};

const urlSlugToAgentType = new Map<string, AgentType>(
  (Object.entries(agentUrlSlugMap) as Array<[AgentType, string]>).map(([type, urlSlug]) => [urlSlug, type])
);

/** Resolves a kebab-case URL segment back to its agent type, or undefined if unknown. */
export const parseAgentUrlSlug = (urlSlug: string): AgentType | undefined => urlSlugToAgentType.get(urlSlug);

/**
 * Returns the icon element associated with an agent slug.
 * Falls back to a sparkle icon for unrecognized slugs.
 */
export const getAgentIcon = (slug: AgentType): ReactNode => {
  const Icon = agentIconMap[slug] ?? LuSparkle;
  return <Icon size={26} className="blue" />;
};
